import { useEffect, useState } from 'react'
import { Button } from '../../components/ui/button'
import { styles } from '../../styles/tailwind-config'
import { AlertTriangle, ArrowRight } from 'lucide-react'
import { Label } from '../../components/ui/label'
import { Input } from '../../components/ui/input'
import Checkbox from '../../components/ui/checkbox'
import { Link, useNavigate } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import axios from '../../lib/axios'
import Spinner from '../../components/ui/spinner'
import { useAuth } from '../../context/AuthProvider';
import boxlogo from '../../assets/logo/logo-mountq-full.svg'
import security from '../../assets/illustrations/technicians-people.svg'

import { toast } from 'sonner'
import { Drawer, DrawerContent, DrawerDescription, DrawerHeader, DrawerTitle } from '../../components/ui/drawer'
import { useTranslation } from 'react-i18next'
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select'
import { setCookie } from '../../lib/cookie'
import { Trans } from '@lingui/react/macro'

const Login = () => {
    const {setAuth, auth, persist, setPersist} = useAuth()
    const {t, i18n} = useTranslation()
    const navigate = useNavigate()

    const [open_multiple_databases, setopen_multiple_databases] = useState(false);

    const [username, setusername] = useState('');
    const [password, setpassword] = useState('');

    const [login_result, setlogin_result] = useState<Userdata | null>(null)

    useEffect(()=>{
        if(auth && auth.status==='authenticated'){ navigate('/') }
    }, [auth])

    const { mutate:handleLogin, reset:resetLogin, isPending, isError } = useMutation({
        mutationFn: async () => {
            const { data } = await axios.post(`authenticate`, {username, password})
            setCookie('mq-token', data.user.token, 1)
            
            if(data.user && data.user.databases && data.user.databases.length > 1){
                setlogin_result(data.user)
                setopen_multiple_databases(true);
            } else {
                setAuth({status:'authenticated', data: data.user})
            }           
        },
        onError: (e) => {
            toast.error(t('login.login_failed'), {id:'handleLogin'})
            console.log(e)
            setTimeout(()=>{resetLogin()}, 2000)
        }
    })

    const handleChangeLanguage = (language:string) => {
        i18n.changeLanguage(language)
    }

    const handlePickDatabase = (database:Database) => {
        const userdata = {...login_result}
        userdata.databases = [database]
        setAuth({status:'authenticated', data: userdata as Userdata});
        window.localStorage.setItem("database-id", database.dbid.toString())
    }

  return (
    <div className='flex flex-col items-center justify-between h-dvh max-h-dvh w-dvw bg-slate-100'>
        <Drawer open={open_multiple_databases} onOpenChange={(o)=>{setopen_multiple_databases(o)}}>
            <DrawerContent>
                <DrawerHeader>
                    <DrawerTitle>{t('login.pick_account')}</DrawerTitle>
                    <DrawerDescription>{t('login.pick_account_description')}</DrawerDescription>
                </DrawerHeader>
                <div className='flex flex-col gap-2 p-4'>
                    {login_result?.databases.map((database, index)=>(
                        <button onClick={()=>{handlePickDatabase(database)}} className='flex items-center justify-between w-full h-12 px-2 font-medium text-left border rounded-md hover:bg-neutral-50' key={index}>
                            {database.name}
                            <ArrowRight className='text-neutral-400'/>
                        </button>
                    ))}
                </div>
            </DrawerContent>
        </Drawer>

        <div className='flex items-center gap-1 py-4'>
            <img className='w-auto h-12' src={boxlogo} alt="MountQ logo"/>
        </div>

        <div className='flex flex-col items-center w-full'>
            <img className='w-full max-w-xs translate-y-10' src={security} alt="MountQ security"/>
            <div className='z-10 flex flex-col items-center w-full max-w-3xl gap-6 p-10 bg-white shadow-2xl md:p-20 rounded-t-3xl'>
                <h1 className={`${styles.h1} w-full`}>{t('login.aanmelden')}</h1>
                <div className='w-full'>
                    <Label htmlFor='username'>{t('login.gebruikersnaam')}</Label>
                    <Input value={username} onChange={({currentTarget})=>{setusername(currentTarget.value)}} onKeyDown={(e)=>{if(e.key === 'Enter'){ handleLogin() }}} placeholder={`${t('login.gebruikersnaam')}...`} id='username' type='email'/>
                </div>
                <div className='w-full'>
                    <div className='flex items-center justify-between'>
                        <Label htmlFor='password'>{t('login.wachtwoord')}</Label>
                        <Link to='/forgot-password' className='text-xs font-medium text-neutral-800'><Trans>Wachtwoord vergeten?</Trans></Link>
                    </div>
                    
                    <Input value={password} onChange={({currentTarget})=>{setpassword(currentTarget.value)}} onKeyDown={(e)=>{if(e.key === 'Enter'){ handleLogin() }}} placeholder={`${t('login.wachtwoord')}...`} id='password' type='password'/>
                </div>
                <div className='flex items-center justify-between w-full gap-4'>
                    <div className='flex items-center w-full gap-1'>
                        <Checkbox checked={persist} onCheckedChange={(v)=>{setPersist(v)}} id='remember'/>
                        <Label htmlFor='remember'>{t('login.onthoud mij')}</Label>
                    </div>
                    <Select value={i18n.language} onValueChange={(v)=>{handleChangeLanguage(v)}}><SelectTrigger className='w-fit'><SelectValue /></SelectTrigger><SelectContent><SelectGroup><SelectItem value='nl'>Nederlands</SelectItem><SelectItem value='fr'>Français</SelectItem><SelectItem value='en'>English</SelectItem></SelectGroup></SelectContent></Select>
                </div>
                <Button onClick={()=>{handleLogin()}} disabled={isPending || isError} className='w-full gap-2'>
                    {isPending && <Spinner className='w-4 h-4'/>}
                    {isError && <AlertTriangle className='w-4 h-4'/>}
                    {t('login.inloggen')}
                </Button>
            </div>
        </div>
    </div>
  )
}

export default Login